/** Decodes an unverified JWT payload for display purposes only (e.g. showing
 * the signed-in email). The API is the actual trust boundary and verifies
 * the access token independently; nothing here is a security decision. */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segment = token.split(".")[1];
  if (!segment) return null;
  try {
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export type CognitoRole = "administrator" | "owner" | "unassigned";

/**
 * Reads Cognito groups for presentation routing only. The API independently
 * verifies the token and enforces every administrator/owner authorization rule.
 */
export function readCognitoRole(token: string | null): CognitoRole {
  if (!token) return "unassigned";
  const payload = decodeJwtPayload(token);
  const claim = payload?.["cognito:groups"];
  const groups = Array.isArray(claim)
    ? claim.map(String)
    : typeof claim === "string"
      ? claim
          .replace(/[[\]"']/g, "")
          .split(/[\s,]+/)
          .map((group) => group.trim())
      : [];
  if (groups.includes("administrators")) return "administrator";
  if (groups.includes("owners")) return "owner";
  return "unassigned";
}
